import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from '../../services/data.service';
import { MessageService, ConfirmationService, ConfirmEventType } from 'primeng/api';
import { PermissionHandlerService } from '../../services/permission-handler.service';
import { TranslatorPipe } from '../../services/translator.pipe';
import { breadcrumb, link } from '../../admin/layout/header/header.component';

interface ProductType {
  _id?: string;
  name: string;
  letter: string;
  active?: boolean;
}

@Component({
  selector: 'app-product-type',
  templateUrl: './product-type.component.html',
  styleUrls: ['./product-type.component.scss']
})
export class ProductTypeComponent implements OnInit {
  breadcrumbs: breadcrumb[] = [
    {
      name: this.translate('Products and Services'),
      link: '/products-view',
      visible: true
    },
    {
      name: this.translate('Product Types'),
      link: '/products-view/product-type',
      visible: true
    }
  ];

  links: link[] = [
    {
      name: this.translate('Back'),
      icon: 'pi pi-arrow-right',
      link: '/products-view',
      visible: true
    }
  ];

  productTypes: ProductType[] = [];
  filteredTypes: ProductType[] = [];
  spinner: boolean = false;
  
  // Form state
  showAddForm: boolean = false;
  isEditMode: boolean = false;
  editingType: ProductType | null = null;
  
  // Form data
  formData: ProductType = {
    name: '',
    letter: '',
    active: true
  };

  // Search
  searchText: string = '';

  constructor(
    private dataService: DataService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public permissionService: PermissionHandlerService,
    private router: Router
  ) { }

  ngOnInit() {
    this.loadProductTypes();
  }

  translate(key: string): string {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }

  async loadProductTypes() {
    this.spinner = true;
    try {
      const response = await this.dataService.getData('product-type');
      
      if (response.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: response.msg || this.translate('Failed to load product types')
        });
        this.productTypes = [];
      } else {
        this.productTypes = response.data || [];
        this.applySearch();
      }
    } catch (error) {
      console.error('Error loading product types:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('An error occurred while loading product types')
      });
      this.productTypes = [];
    } finally {
      this.spinner = false;
    }
  }

  applySearch() {
    if (!this.searchText || this.searchText.trim() === '') {
      this.filteredTypes = [...this.productTypes];
    } else {
      const searchLower = this.searchText.toLowerCase().trim();
      this.filteredTypes = this.productTypes.filter(type =>
        type.name.toLowerCase().includes(searchLower) ||
        type.letter.toLowerCase().includes(searchLower)
      );
    }
  }

  onSearchChange() {
    this.applySearch();
  }

  openAddForm() {
  
    this.isEditMode = false;
    this.editingType = null;
    this.formData = {
      name: '',
      letter: '',
      active: true
    };
    this.showAddForm = true;
  }

  openEditForm(type: ProductType) {
    if (!this.permissionService.hasPermission('PRODUCT_TYPE', 'u')) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Access Denied'),
        detail: this.translate('You do not have permission to update product types')
      });
      return;
    }
    this.isEditMode = true;
    this.editingType = type;
    this.formData = {
      name: type.name,
      letter: type.letter,
      active: type.active !== undefined ? type.active : true
    };
    this.showAddForm = true;
  }

  closeForm() {
    this.showAddForm = false;
    this.isEditMode = false;
    this.editingType = null;
    this.formData = {
      name: '',
      letter: '',
      active: true
    };
  }

  async onSubmit() {
    // Validation
    if (!this.formData.name || this.formData.name.trim() === '') {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Validation Error'),
        detail: this.translate('Name is required')
      });
      return;
    }

    if (!this.formData.letter || this.formData.letter.trim() === '') {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Validation Error'),
        detail: this.translate('Letter is required')
      });
      return;
    }

    if (this.formData.letter.length !== 1) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Validation Error'),
        detail: this.translate('Letter must be a single character')
      });
      return;
    }

    this.spinner = true;
    try {
      if (this.isEditMode && this.editingType?._id) {
        // Update
        const updateData = {
          _id: this.editingType._id,
          name: this.formData.name.trim(),
          letter: this.formData.letter.trim().toUpperCase(),
          active: this.formData.active
        };

        const response = await this.dataService.postData('product-type', updateData, 'PUT');
        
        if (response.error) {
          this.messageService.add({
            severity: 'error',
            summary: this.translate('Error'),
            detail: response.msg || this.translate('Failed to update product type')
          });
        } else {
          this.messageService.add({
            severity: 'success',
            summary: this.translate('Success'),
            detail: this.translate('Product type updated successfully')
          });
          this.closeForm();
          await this.loadProductTypes();
        }
      } else {
        // Create
        const createData = {
          name: this.formData.name.trim(),
          letter: this.formData.letter.trim().toUpperCase(),
          active: this.formData.active !== undefined ? this.formData.active : true
        };

        const response = await this.dataService.postData('product-type', createData, 'POST');
        
        if (response.error) {
          this.messageService.add({
            severity: 'error',
            summary: this.translate('Error'),
            detail: response.msg || this.translate('Failed to create product type')
          });
        } else {
          this.messageService.add({
            severity: 'success',
            summary: this.translate('Success'),
            detail: this.translate('Product type created successfully')
          });
          this.closeForm();
          await this.loadProductTypes();
        }
      }
    } catch (error) {
      console.error('Error submitting product type:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('An error occurred while saving the product type')
      });
    } finally {
      this.spinner = false;
    }
  }

  deleteType(type: ProductType) {
    if (!this.permissionService.hasPermission('PRODUCT_TYPE', 'd')) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Access Denied'),
        detail: this.translate('You do not have permission to delete product types')
      });
      return;
    }

    this.confirmationService.confirm({
      message: this.translate('Are you sure you want to delete this product type?'),
      header: this.translate('Confirm Delete'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate('نعم'),
      rejectLabel: this.translate('لا'),
      accept: async () => {
        await this.performDelete(type._id!);
        return true;
      },
      reject: (type: any) => {
        return false;
      },
    });
  }

  async performDelete(id: string) {
    this.spinner = true;
    try {
      const response = await this.dataService.deleteData(`product-type/${id}`);
      
      if (response.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: response.msg || this.translate('Failed to delete product type')
        });
      } else {
        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate('Product type deleted successfully')
        });
        await this.loadProductTypes();
      }
    } catch (error) {
      console.error('Error deleting product type:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('An error occurred while deleting the product type')
      });
    } finally {
      this.spinner = false;
    }
  }

  getActiveCount(): number {
    return this.productTypes.filter(type => type.active).length;
  }

  getInactiveCount(): number {
    return this.productTypes.filter(type => !type.active).length;
  }
}

